import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  financeStructureApi,
  type FinanceCategory,
  type NedarimMappingRow,
} from "../../api/financeStructureApi";
import { toClientApiError } from "../../api/httpClient";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { QuietButton } from "../../ui/QuietButton";

type Props = { writeCustomerId: string };

type NedarimRow = NedarimMappingRow & { rowKey: string };

export function NedarimMappingsTab({ writeCustomerId }: Props) {
  const [rows, setRows] = useState<NedarimRow[]>([]);
  const [categories, setCategories] = useState<FinanceCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [groupe, setGroupe] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, cats] = await Promise.all([
        financeStructureApi.listNedarimMappings(),
        financeStructureApi.listCategories(),
      ]);
      setRows(
        data.rows.map((r) => ({
          ...r,
          rowKey: `${r.customerId}::${r.nedarimGroupe}`,
        })),
      );
      setCategories(cats.filter((c) => !c.isSystem));
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "nedarimGroupe",
        title: "קבוצת נדרים",
        sortable: true,
        filterable: true,
        minWidth: 180,
      },
      {
        key: "donationCount",
        title: "תרומות",
        sortable: true,
        minWidth: 80,
      },
      {
        key: "categoryId",
        title: "קטגוריה",
        minWidth: 200,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const r = row as unknown as NedarimRow;
          return (
            <FormField
              type="select"
              value={r.categoryId ?? ""}
              onChange={(next) => {
                if (!next) {
                  if (r.id) {
                    void financeStructureApi
                      .deleteNedarimMapping(r.customerId, r.id)
                      .then(load)
                      .catch((err) => setError(toClientApiError(err).message));
                  }
                  return;
                }
                if (r.id) {
                  void financeStructureApi
                    .updateNedarimMapping(r.customerId, r.id, next)
                    .then(load)
                    .catch((err) => setError(toClientApiError(err).message));
                } else {
                  void financeStructureApi
                    .createNedarimMapping(r.customerId, {
                      nedarimGroupe: r.nedarimGroupe,
                      categoryId: next,
                    })
                    .then(load)
                    .catch((err) => setError(toClientApiError(err).message));
                }
              }}
              options={[
                { value: "", label: "— ללא מיפוי —" },
                ...categories.map((c) => ({
                  value: c.id,
                  label: c.displayName ?? c.name,
                })),
              ]}
            />
          );
        },
      },
      {
        key: "isMapped",
        title: "סטטוס",
        render: (_value: unknown, row: Record<string, unknown>) =>
          row.isMapped ? "ממופה" : "לא ממופה",
      },
      {
        key: "actions",
        title: "",
        render: (_value: unknown, row: Record<string, unknown>) => {
          const r = row as unknown as NedarimRow;
          if (!r.id) return null;
          return (
            <QuietButton
              type="button"
              onClick={() =>
                void financeStructureApi
                  .deleteNedarimMapping(r.customerId, r.id!)
                  .then(load)
                  .catch((err) => setError(toClientApiError(err).message))
              }
            >
              מחק מיפוי
            </QuietButton>
          );
        },
      },
    ],
    [categories, load],
  );

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="מיפוי ידני"
        onPrimary={() => {
          setGroupe("");
          setCategoryId(categories[0]?.id ?? "");
          setCreateOpen(true);
        }}
      />
      {error ? <p className="form-error">{error}</p> : null}
      <p style={{ color: "var(--ck-text-2)", fontSize: "0.9rem" }}>
        קבוצות נדרים מופיעות מתרומות מסונכרנות או ממיפוי ידני. סנכרון חיצוני מחוץ לגל זה.
      </p>
      {loading ? <p>טוען…</p> : null}
      {!loading ? (
        <TableKit
          columns={columns}
          rows={rows as unknown as Record<string, unknown>[]}
          rowKey="rowKey"
          emptyMessage="אין קבוצות נדרים — הוסיפו מיפוי ידני או סנכרנו תרומות"
        />
      ) : null}

      <FormDialog open={createOpen} onClose={() => setCreateOpen(false)} size="md">
        <FormCard
          title="מיפוי נדרים ידני"
          onSave={() => {
            void (async () => {
              setSaving(true);
              try {
                await financeStructureApi.createNedarimMapping(writeCustomerId, {
                  nedarimGroupe: groupe.trim(),
                  categoryId,
                });
                setCreateOpen(false);
                await load();
              } catch (err) {
                setError(toClientApiError(err).message);
              } finally {
                setSaving(false);
              }
            })();
          }}
          onCancel={() => setCreateOpen(false)}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={saving || !groupe.trim() || !categoryId}
        >
          <FormSection title="מיפוי">
            <FormGrid>
              <FormField
                label="קבוצת נדרים"
                required
                value={groupe}
                onChange={setGroupe}
              />
              <FormField
                label="קטגוריה"
                required
                value={categoryId}
                onChange={setCategoryId}
                options={categories.map((c) => ({
                  value: c.id,
                  label: c.displayName ?? c.name,
                }))}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </div>
  );
}
